import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { browserId } from '../lib/browserId';
import { generateWordsForDuration } from '../data/wordList';
import { generatePassagesForDuration } from '../data/practicePassages';
import { useTypingTest } from '../hooks/useTypingTest';
import { AnimatedNumber } from './AnimatedNumber';

export type PracticeMode = 'passages' | 'words';

interface PracticeTypingTestProps {
  duration: number;
  mode: PracticeMode;
}

const generateText = (mode: PracticeMode, duration: number) =>
  mode === 'words' ? generateWordsForDuration(duration) : generatePassagesForDuration(duration);

const PracticeTypingTest: React.FC<PracticeTypingTestProps> = ({ duration, mode }) => {
  const [text, setText] = useState(() => generateText(mode, duration));
  const [refreshSpinning, setRefreshSpinning] = useState(false);
  const [textFading, setTextFading] = useState(false);

  // Inline results shown when test ends
  const [finalWPM, setFinalWPM] = useState(0);
  const [finalAccuracy, setFinalAccuracy] = useState(0);
  const [finalChars, setFinalChars] = useState({ correct: 0, total: 0 });

  const handleEnd = useCallback((input: string, actualDuration: number) => {
    const typedChars = input.length;
    const correctChars = input.split('').filter((c, i) => c === text[i]).length;
    const netWPM = actualDuration > 0
      ? Math.max(0, Math.round((correctChars / 5) / (actualDuration / 60)))
      : 0;
    const accuracy = typedChars > 0 ? Math.round((correctChars / typedChars) * 100) : 0;
    setFinalWPM(netWPM);
    setFinalAccuracy(accuracy);
    setFinalChars({ correct: correctChars, total: typedChars });

    // Anonymous usage stats for the admin; the run's result above never depends on this
    if (typedChars >= 5) {
      axios.post('/api/practice/sessions', {
        browser_id: browserId(),
        mode,
        duration,
        elapsed_seconds: actualDuration,
        typed_chars: typedChars,
        correct_chars: correctChars,
      }, { timeout: 10000 }).catch(() => {});
    }
  }, [text, mode, duration]);

  const {
    testEnded,
    hasInputError,
    isFocused,
    setIsFocused,
    lineTransition,
    textBlockWidth,
    displayWPM,
    currentWord,
    renderVisibleText,
    inputRef,
    containerRef,
    handleKeyDown,
    handleInputChange,
    handleContainerClick,
    formatTime,
    timeLeft,
    testStarted,
    reset,
  } = useTypingTest({ text, duration, onEnd: handleEnd });

  const loadWords = useCallback(() => {
    setText(generateText(mode, duration));
  }, [mode, duration]);

  const resetTest = useCallback(() => {
    setRefreshSpinning(true);
    setTimeout(() => setRefreshSpinning(false), 500);

    setTextFading(true);

    setTimeout(() => {
      setFinalWPM(0);
      setFinalAccuracy(0);
      setFinalChars({ correct: 0, total: 0 });

      reset();
      if (inputRef.current) inputRef.current.value = '';
      loadWords();

      setTimeout(() => {
        setTextFading(false);
        if (inputRef.current) inputRef.current.focus();
      }, 15);
    }, 50);
  }, [reset, loadWords, inputRef]);

  if (!text) {
    return (
      <div className="typing-test">
        <div className="text-display skeleton-display" />
        <div className="stats-bar">
          <div className="input-display" style={{ flex: 1, height: 48 }} />
          <div className="stat-box wpm-box" />
          <div className="stat-box timer-box" />
          <div className="stat-box refresh-box" />
        </div>
      </div>
    );
  }

  return (
    <div className="typing-test">
      {/* Text Display */}
      <div
        ref={containerRef}
        onClick={handleContainerClick}
        className={`text-display${textFading ? ' text-fading' : ''}`}
      >
        {testEnded ? (
          <div className="inline-results">
            <div className="inline-result-item">
              <span className="inline-result-value"><AnimatedNumber value={finalWPM} /></span>
              <span className="inline-result-label">WPM</span>
            </div>
            <div className="inline-result-item">
              <span className="inline-result-value"><AnimatedNumber value={finalAccuracy} suffix="%" /></span>
              <span className="inline-result-label">Accuracy</span>
            </div>
            <div className="inline-result-item">
              <span className="inline-result-value"><AnimatedNumber value={finalChars.correct} /></span>
              <span className="inline-result-label">Correct</span>
            </div>
            <div className="inline-result-item">
              <span className="inline-result-value"><AnimatedNumber value={finalChars.total - finalChars.correct} /></span>
              <span className="inline-result-label">Errors</span>
            </div>
          </div>
        ) : (
          <div
            className={`text-lines-inner${lineTransition ? ' sliding' : ''}`}
            style={textBlockWidth > 0 ? { width: `${textBlockWidth}px` } : undefined}
          >
            {renderVisibleText}
          </div>
        )}
      </div>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div
          className={`input-display${isFocused ? ' focused' : ''}${hasInputError ? ' input-error' : ''}`}
          onClick={handleContainerClick}
        >
          <span className="input-text">{currentWord}</span>
          <span className={`input-cursor${testEnded || !isFocused ? ' hidden' : ''}`} />
        </div>

        <div className="stat-box wpm-box">
          <span className="stat-value">{testEnded ? finalWPM : displayWPM}</span>
          <span className="stat-label">WPM</span>
        </div>

        <div className="stat-box timer-box">
          <span className={`stat-value${timeLeft <= 5 && testStarted && !testEnded ? ' warning' : ''}`}>
            {formatTime(testEnded ? 0 : timeLeft)}
          </span>
        </div>

        <div
          className={`stat-box refresh-box${refreshSpinning ? ' spinning' : ''}`}
          onClick={resetTest}
          role="button"
          aria-label="New text"
          title="New text"
        >
          <svg
            className="refresh-icon"
            width="20" height="20" viewBox="0 0 24 24"
            fill="none" stroke="currentColor"
            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="M21.5 2v6h-6" />
            <path d="M2.5 22v-6h6" />
            <path d="M3.5 8a9 9 0 0 1 14.68-3.19L21.5 8" />
            <path d="M20.5 16a9 9 0 0 1-14.68 3.19L2.5 16" />
          </svg>
        </div>
      </div>

      {/* Hidden real input */}
      <form autoComplete="off" onSubmit={e => e.preventDefault()} className="hidden-input-form">
        <input
          ref={inputRef}
          type="text"
          onKeyDown={handleKeyDown}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          disabled={testEnded}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          autoFocus
          className="hidden-input"
        />
      </form>
    </div>
  );
};

export default PracticeTypingTest;
